import { CachePolicies, useFetch } from 'use-http';
import { routes } from '@/webapi/routes';
import {
  AiChatMessage,
  ReasoningMessage,
  SelectedAgent,
  ToolCall,
} from '@/features/ai-agents/shared/models';
import { getJsonMarkdown } from '@/features/ai-agents/shared/message-format-processor';
import { useAIAuthHeaders } from '@/features/ai-agents/shared/use-ai-auth-headers';

export function useAiMetadataApi(isLocalHost = false) {
  const { headers } = useAIAuthHeaders(isLocalHost);

  const { get, post, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      headers,
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: true,
    },
  }));

  const status = async (): Promise<boolean> => {
    const resp = await get(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiPlaygroundStatus())
        : routes.aiPlaygroundStatus(),
    );
    return resp?.playground === `available`;
  };

  const listAll = async (): Promise<PlaygroundResources> => {
    const agents = await get(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiPlaygroundListAgents())
        : routes.aiPlaygroundListAgents(),
    );
    const teams = await get(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiPlaygroundListTeams())
        : routes.aiPlaygroundListTeams(),
    );
    const workflows = await get(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiPlaygroundListWorkflows())
        : routes.aiPlaygroundListWorkflows(),
    );
    return { agents, teams, workflows };
  };

  const listSessions = async (
    selectedAgent: SelectedAgent,
    userId: string,
  ): Promise<SessionListEntry[]> =>
    get(
      isLocalHost
        ? routes.convertToLocalAiUrl(
            routes.aiPlaygroundListAgentSessions(
              `${selectedAgent?.type}s`,
              selectedAgent?.id,
              userId,
            ),
          )
        : routes.aiPlaygroundListAgentSessions(
            `${selectedAgent?.type}s`,
            selectedAgent?.id,
            userId,
          ),
    );

  const getSessionCheckpoints = async (
    initialSectionId: string,
  ): Promise<Array<HistoryCheckpoint>> => {
    const resp = await get(
      isLocalHost
        ? routes.convertToLocalAiUrl(
            routes.aiGetSessionCheckpoints(initialSectionId),
          )
        : routes.aiGetSessionCheckpoints(initialSectionId),
    );
    return resp?.checkpoints || [];
  };

  const restoreSessionCheckpoint = async (
    initialSectionId: string,
    selectedAgent: SelectedAgent,
  ): Promise<any> => {
    const resp = await post(
      isLocalHost
        ? routes.convertToLocalAiUrl(
            routes.aiRestoreSessionCheckpoint(selectedAgent, initialSectionId),
          )
        : routes.aiRestoreSessionCheckpoint(selectedAgent, initialSectionId),
    );
    return resp;
  };

  const getSession = async (
    agent: SelectedAgent,
    sessionId: string,
    userId?: string,
  ): Promise<AiChatMessage[]> => {
    const response = await get(
      isLocalHost
        ? routes.convertToLocalAiUrl(
            routes.aiGetAgentSession(agent, sessionId, userId),
          )
        : routes.aiGetAgentSession(agent, sessionId, userId),
    );

    if (response && response.chat_history) {
      const sessionHistory = response.chat_history;
      if (sessionHistory && Array.isArray(sessionHistory)) {
        const messagesForPlayground = sessionHistory
          .filter((h) => !!h.content)
          .flatMap((run) => {
            const filteredMessages: AiChatMessage[] = [];

            if (run.role === `user`) {
              filteredMessages.push({
                role: `user`,
                content: run.content ?? ``,
                created_at: run.created_at,
                images: run.images,
              });
            }

            if (run.role === `assistant`) {
              const toolCalls = [
                ...(run.tools ?? []),
                ...(run.extra_data?.reasoning_messages ?? []).reduce(
                  (acc: ToolCall[], msg: ReasoningMessage) => {
                    if (msg.role === `tool`) {
                      acc.push({
                        role: msg.role,
                        content: msg.content,
                        tool_call_id: msg.tool_call_id ?? ``,
                        tool_name: msg.tool_name ?? ``,
                        tool_args: msg.tool_args ?? {},
                        tool_call_error: msg.tool_call_error ?? false,
                        metrics: msg.metrics ?? { time: 0 },
                        created_at:
                          msg.created_at ?? Math.floor(Date.now() / 1000),
                      });
                    }
                    return acc;
                  },
                  [],
                ),
              ];

              filteredMessages.push({
                role: `agent`,
                content: (run.content as string) ?? ``,
                tool_calls: toolCalls.length > 0 ? toolCalls : undefined,
                extra_data: run.extra_data,
                images: run.images,
                videos: run.videos,
                audio: run.audio,
                response_audio: run.response_audio,
                created_at: run.created_at,
              });
            }

            return filteredMessages;
          });

        const processedMessages = messagesForPlayground.map(
          (message: AiChatMessage) => {
            if (Array.isArray(message.content)) {
              const textContent = message.content
                .filter((item: { type: string }) => item.type === `text`)
                .map((item) => item.text)
                .join(` `);

              return {
                ...message,
                content: textContent,
              };
            }
            if (typeof message.content !== `string`) {
              return {
                ...message,
                content: getJsonMarkdown(message.content),
              };
            }
            return message;
          },
        );
        return processedMessages;
      }
    }
    return [];
  };

  const askQuestion = async (
    agent: SelectedAgent,
    question: string,
  ): Promise<any> => {
    const formData = new FormData();
    formData.append(`message`, question);
    formData.append(`stream`, `false`);
    formData.append(`session_id`, ``);
    formData.append(`user_id`, ``);

    const response = await fetch(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiAgentRun(agent))
        : routes.aiAgentRun(agent),
      {
        headers: {
          ...headers,
        },
        method: `POST`,
        body: formData,
        credentials: `include`,
      },
    );
    if (response.ok) {
      return response.json();
    }
    throw new Error(`Error: ${response.status} ${response.statusText}`);
  };

  return {
    status,
    listAll,
    listSessions,
    getSession,
    askQuestion,
    getSessionCheckpoints,
    restoreSessionCheckpoint,
    loading,
  };
}

export interface PlaygroundResources {
  agents: Agent[];
  teams: Team[];
  workflows: Workflow[];
}

export interface Model {
  name: string;
  model: string;
  provider: string;
}

export interface PropertyDescription {
  type: string;
  description: string;
}

export interface ParametersObject {
  type: string;
  properties: Record<string, PropertyDescription>;
  required: string[];
}

export interface Tool {
  name: string;
  description: string;
  parameters: ParametersObject;
  requires_confirmation: boolean;
  external_execution: boolean;
}

export interface MemoryDb {
  name: string;
  collection_name: string;
  db_name: string;
}

export interface Memory {
  name: string;
  model: Model;
  db: MemoryDb;
}

export interface Storage {
  name: string;
}

export interface Knowledge {
  name: string;
}

export interface Agent {
  agent_id: string;
  name: string;
  model: Model;
  add_context: boolean;
  tools: Tool[];
  memory: Memory;
  storage: Storage;
  knowledge: Knowledge;
  description: string;
  instructions: string[];
}

export type TeamMember = Agent;

export interface Team {
  team_id: string;
  name: string;
  description: string;
  mode: string; // "coordinate" or other possible values
  model: Model; // Reusing the existing Model interface
  success_criteria: string | null;
  instructions: string[];
  members: TeamMember[];
}

export interface Workflow {
  workflow_id: string;
  name: string;
  description: string;
  model: Model;
}

export interface SessionListEntry {
  title: string;
  session_id: string;
  session_name: string;
  created_at: number;
}

export interface HistoryCheckpoint {
  agent_id: string;
  run_id: string;
  input: string;
  html: string;
  css: string;
  js: string;
  first_session_id: string;
  checkpoint_session_id: string;
  new_session_id: string;
  user: string;
  alias: string;
  timestamp: number;
  date?: Date;
  dateString?: string;
  isSelected?: boolean;
}
